import { IPagination } from '@/types/general.type';
import { ApiResponse, axiosInstance } from '@/lib/axios';
import { QueryConfig } from '@/lib/react-query';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { TAgent } from '../types/agent.type';
import { TFetchParams } from '@/types/fetch.type';

export type TGetListAgentResponse = ApiResponse<IPagination<TAgent>>;
export type TGetListAgentParams = TFetchParams & {
  teamId?: string;
  roleId?: string;
};

export const getListAgent = async (params: TGetListAgentParams): Promise<TGetListAgentResponse> => {
  const url = 'user/manage';

  return axiosInstance.get(url, { params });
};

export const GET_LIST_AGENT_QUERY_KEY = 'get-list-agent';

export const getListAgentQueryOptions = <TData = IPagination<TAgent>>(
  params: TGetListAgentParams,
  select?: (data: TGetListAgentResponse) => TData
) => {
  return queryOptions({
    queryKey: [
      GET_LIST_AGENT_QUERY_KEY,
      {
        search: params?.search,
        page: params?.page,
        limit: params?.limit,
        sortDir: params?.sortDir,
        sortBy: params?.sortBy,
        teamId: params?.teamId,
        roleId: params?.roleId,
      },
    ],
    queryFn: () => getListAgent(params),
    select,
  });
};

// Overload when select is provided - returns transformed type
export function useGetListAgentQuery<TData>(options: {
  params: TGetListAgentParams;
  queryConfig?: QueryConfig<typeof getListAgentQueryOptions<TData>>;
  select: (data: TGetListAgentResponse) => TData;
}): ReturnType<typeof useQuery<TData>>;

// Overload when select is not provided - returns raw API response (even if generic is provided)
export function useGetListAgentQuery(options: {
  params: TGetListAgentParams;
  queryConfig?: QueryConfig<typeof getListAgentQueryOptions<TGetListAgentResponse>>;
}): ReturnType<typeof useQuery<TGetListAgentResponse>>;

// Implementation
export function useGetListAgentQuery<TData = TGetListAgentResponse>({
  queryConfig,
  params,
  select,
}: {
  params: TGetListAgentParams;
  queryConfig?: QueryConfig<typeof getListAgentQueryOptions<TData | TGetListAgentResponse>>;
  select?: (data: TGetListAgentResponse) => TData;
}) {
  return useQuery({
    ...getListAgentQueryOptions(params, select),
    ...queryConfig,
  }) as ReturnType<typeof useQuery<TData extends TGetListAgentResponse ? TGetListAgentResponse : TData>>;
}
